import { getChromeRuntime, getDocument } from '../chrome/wrappers';
import { ChromeMessageType } from '../types';
import { timeoutPromise } from '../helper';
import { VERDICT_TIMEOUT_MS } from '../navigation/verdictTimeout';
import { isProtectionEnabled } from '../protectionStatus';
import { isDelegationStartedOrNotLoggedInWithDelegation } from '../delegation/delegation';
import { isSafeNetworkDelegated } from '../safeNetworks/safeNetworksDelegation';
import { captureException } from '../helper/sentry';

// CSS opacity values
const OPACITY_HIDDEN = '0';
const OPACITY_INITIAL = 'initial';

// Always hide by default
let shouldHide = true;

export const shouldEnableContentHiding = () =>
  isProtectionEnabled() &&
  isDelegationStartedOrNotLoggedInWithDelegation() &&
  !isSafeNetworkDelegated();

/**
 * Hide page content and restore it after the navigation verdict is processed by the service.
 * If the verdict is not received (e.g. an error occurred), the content will be restored after a while.
 */
export const setupContentHiding = () => {
  // Actually hide the content
  hideContent();
  // Restore page content when message is received from the service
  restoreContentWhenPossible();
};

const restoreContentWhenPossible = () => {
  try {
    getChromeRuntime().sendMessage(
      { type: ChromeMessageType.SHOULD_RESTORE_CONTENT },
      response => {
        if (response?.restore as boolean) {
          restoreContentIfNeeded();
        }
      }
    );
  } catch {
    restoreContentIfNeeded();
  }
};

const hideContent = () => {
  setDocumentOpacityIfNeeded();
  // Restore content again after 5 seconds, in case a crash prevented it from being restored
  enqueueContentRestore();
};

const isDocumentOpacityHidden = () =>
  getDocument()?.documentElement?.style?.opacity === OPACITY_HIDDEN;

const setDocumentOpacity = (opacity: string) => {
  getDocument().documentElement.style.opacity = opacity;
};

const setDocumentOpacityIfNeeded = () => {
  if (shouldHide) {
    try {
      setDocumentOpacity(OPACITY_HIDDEN);
    } catch (error) {
      // If the document is not available,
      // we want to know and let the rest of the logic run.
      console.error(error);
      captureException(error as Error);
    }
  }
};

const enqueueContentRestore = () =>
  timeoutPromise(VERDICT_TIMEOUT_MS, restoreDocumentOpacityIfNeeded);

export const restoreContentIfNeeded = () => {
  shouldHide = false;
  restoreDocumentOpacityIfNeeded();
};

const restoreDocumentOpacityIfNeeded = () => {
  if (isDocumentOpacityHidden()) {
    setDocumentOpacity(OPACITY_INITIAL);
  }
};
